// ---------------------------------------------------------------------------
// storeSnapshot — last-good copy of the store's data in IndexedDB
// (2026-08-26, Jaylen's dead-signal walkthrough).
//
// The offline story has three legs:
//   1. public/sw.js caches the APP SHELL so the SPA can boot offline.
//   2. THIS module caches the DATA: after every successful loadAll the
//      whole store payload (clients, estimates, jobs, crew, trucks,
//      config, availability, user profiles) is written here; when
//      loadAll can't reach Supabase, the store hydrates from the
//      snapshot instead of dead-ending on the error screen.
//   3. estimatorQueue (and portalQueue) park WRITES until signal returns.
//
// Practical effect: open the app on office wifi in the morning → it
// boots and shows data in a no-signal basement that afternoon, and
// walkthrough saves queue + sync on the drive out.
//
// Same IndexedDB degrade rules as the queues: no IndexedDB (private
// mode) → no snapshot, exactly the old behavior, never worse.
// ---------------------------------------------------------------------------

const DB_NAME = 'bbm_store_snapshot';
const STORE = 'snapshot';
const DB_VERSION = 1;
const KEY = 'latest';

function idbAvailable() {
  return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Persist the latest full data payload. Fire-and-forget from the store —
// a failed write must never affect the live app.
export async function saveStoreSnapshot(payload) {
  if (!idbAvailable()) return false;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ ...payload, savedAt: new Date().toISOString() }, KEY);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    db.close();
    return true;
  } catch (e) {
    console.warn('[snapshot] save failed:', e?.message || e);
    return false;
  }
}

// Load the last-good payload, or null when none exists / IDB unavailable.
export async function loadStoreSnapshot() {
  if (!idbAvailable()) return null;
  try {
    const db = await openDb();
    const row = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return row;
  } catch (e) {
    console.warn('[snapshot] load failed:', e?.message || e);
    return null;
  }
}
